import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import PageHeader from "../../../components/DashboardComponents/PageHeader"
import DashboardCard from "../../../components/DashboardComponents/DashboardCard"
import ListItem from "../../../components/DashboardComponents/ListItem"
import CustomButton from "../../../components/OnboardingComponents/CustomButton"
import EmptyState from "../../../components/Common/EmptyState"
import { ICONS } from "../../../constants/icons"
import { get } from "../../../api/axios"
import endpoints from "../../../api/endpoints"
import { toastApiError } from "../../../helpers/apiError"
import { formatDashboardGreeting } from "../../../helpers/timeGreeting"
import { getUserFullName } from "../../../helpers/userDisplay"
import useAdminPermission from "../../../hooks/useAdminPermission"
import PlatformOverviewChart from "./PlatformOverviewChart"
import MembersByCityDonut from "./MembersByCityDonut"
import {
  ACTIVE_COLLABORATORS_LIMIT,
  ACTIVE_COLLABORATORS_WITH,
  DEFAULT_PERIOD,
  mapActiveCities,
  mapActiveCollaborators,
  mapKpiCards,
  mapMembersByCity,
  mapModuleUsage,
  mapPlatformOverview,
  mapRecentActivity,
  PERIOD_OPTIONS,
} from "./adminDashboardData"

const cityInitials = name =>
  (name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase())
    .join("")

/**
 * Admin Dashboard — wires stats, platform overview, and module usage APIs.
 * Other sections stay on fixtures until their endpoints land.
 */
const AdminDashboard = () => {
  const navigate = useNavigate()
  const currentUser = useSelector(state => state.auth.currentUser)
  const { can } = useAdminPermission()
  const canViewActivityLogs = can("activity-logs", "view")
  const canViewCities = can("cities", "view")
  const canViewMembers = can("members", "view")
  const [period, setPeriod] = useState(DEFAULT_PERIOD)
  const [periodOpen, setPeriodOpen] = useState(false)
  const periodRef = useRef(null)

  const [kpiCards, setKpiCards] = useState(() => mapKpiCards())
  const [platformOverview, setPlatformOverview] = useState(() =>
    mapPlatformOverview(),
  )
  const [moduleUsage, setModuleUsage] = useState([])
  const [activeCities, setActiveCities] = useState([])
  const [membersByCity, setMembersByCity] = useState(() => mapMembersByCity())
  const [recentActivity, setRecentActivity] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isCitiesLoading, setIsCitiesLoading] = useState(true)
  const [isActivityLoading, setIsActivityLoading] = useState(true)
  const [activeCollaborators, setActiveCollaborators] = useState([])
  const [isCollaboratorsLoading, setIsCollaboratorsLoading] = useState(true)
  const [error, setError] = useState(null)

  const periodLabel = useMemo(
    () =>
      PERIOD_OPTIONS.find(option => option.value === period)?.label || period,
    [period],
  )

  useEffect(() => {
    document.title = "Dashboard | AMOK . EARTH Admin"
  }, [])

  useEffect(() => {
    if (!periodOpen) return undefined
    const handlePointer = event => {
      if (periodRef.current && !periodRef.current.contains(event.target)) {
        setPeriodOpen(false)
      }
    }
    document.addEventListener("mousedown", handlePointer)
    return () => document.removeEventListener("mousedown", handlePointer)
  }, [periodOpen])

  const fetchDashboard = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    const params = { period }

    try {
      const [statsRes, overviewRes, modulesRes] = await Promise.all([
        get(endpoints.getAdminDashboardStats, { params }),
        get(endpoints.getAdminPlatformOverview, { params }),
        get(endpoints.getAdminModuleUsage, { params }),
      ])

      if (statsRes?.data?.success) {
        const result = statsRes.data.result || {}
        setKpiCards(mapKpiCards(result, result.growth || {}))
      } else {
        toastApiError(statsRes, "Failed to load dashboard stats")
      }

      if (overviewRes?.data?.success) {
        setPlatformOverview(
          mapPlatformOverview(overviewRes.data.result || {}, period),
        )
      } else {
        toastApiError(overviewRes, "Failed to load platform overview")
      }

      if (modulesRes?.data?.success) {
        setModuleUsage(mapModuleUsage(modulesRes.data.result || {}))
      } else {
        toastApiError(modulesRes, "Failed to load module usage")
      }

      if (
        !statsRes?.data?.success &&
        !overviewRes?.data?.success &&
        !modulesRes?.data?.success
      ) {
        setError("Unable to load dashboard data.")
      }
    } catch (err) {
      const message = toastApiError(err, "Failed to load dashboard")
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }, [period])

  useEffect(() => {
    fetchDashboard()
  }, [fetchDashboard])

  useEffect(() => {
    let cancelled = false
    const fetchActiveCities = async () => {
      if (!canViewCities) {
        setActiveCities([])
        setIsCitiesLoading(false)
        return
      }
      setIsCitiesLoading(true)
      try {
        const response = await get(endpoints.getAdminCities, {
          params: { page_size: 5, page: 1 },
        })
        if (cancelled) return
        if (response?.data?.success) {
          setActiveCities(mapActiveCities(response.data.result || {}))
        } else {
          toastApiError(response, "Failed to load cities")
          setActiveCities([])
        }
      } catch (err) {
        if (!cancelled) {
          toastApiError(err, "Failed to load cities")
          setActiveCities([])
        }
      } finally {
        if (!cancelled) setIsCitiesLoading(false)
      }
    }
    fetchActiveCities()
    return () => {
      cancelled = true
    }
  }, [canViewCities])

  useEffect(() => {
    let cancelled = false
    const fetchMembersByCity = async () => {
      if (!canViewMembers) {
        setMembersByCity(mapMembersByCity())
        return
      }
      try {
        const response = await get(endpoints.getAdminMembersByCity)
        if (cancelled) return
        if (response?.data?.success) {
          setMembersByCity(mapMembersByCity(response.data.result || {}))
        } else {
          toastApiError(response, "Failed to load members by city")
        }
      } catch (err) {
        if (!cancelled) {
          toastApiError(err, "Failed to load members by city")
        }
      }
    }
    fetchMembersByCity()
    return () => {
      cancelled = true
    }
  }, [canViewMembers])

  useEffect(() => {
    let cancelled = false
    const fetchRecentActivity = async () => {
      if (!canViewActivityLogs) {
        setRecentActivity([])
        setIsActivityLoading(false)
        return
      }
      setIsActivityLoading(true)
      try {
        const response = await get(endpoints.getAdminActivityLogs, {
          params: {
            search: "",
            sort_by: "created_at",
            sort_direction: "desc",
            page_size: 5,
          },
        })
        if (cancelled) return
        if (response?.data?.success) {
          setRecentActivity(mapRecentActivity(response.data.result || {}, 5))
        } else {
          toastApiError(response, "Failed to load recent activity")
          setRecentActivity([])
        }
      } catch (err) {
        if (!cancelled) {
          toastApiError(err, "Failed to load recent activity")
          setRecentActivity([])
        }
      } finally {
        if (!cancelled) setIsActivityLoading(false)
      }
    }
    fetchRecentActivity()
    return () => {
      cancelled = true
    }
  }, [canViewActivityLogs])

  useEffect(() => {
    let cancelled = false
    const fetchActiveCollaborators = async () => {
      if (!canViewMembers) {
        setActiveCollaborators([])
        setIsCollaboratorsLoading(false)
        return
      }
      setIsCollaboratorsLoading(true)
      try {
        const response = await get(endpoints.getAdminMembers, {
          params: {
            search: "",
            sort_by: "total_xp",
            sort_direction: "desc",
            page: 1,
            page_size: ACTIVE_COLLABORATORS_LIMIT,
            per_page: ACTIVE_COLLABORATORS_LIMIT,
            "with[]": ACTIVE_COLLABORATORS_WITH,
          },
          skipErrorToast: true,
        })
        if (cancelled) return
        if (response?.data?.success) {
          setActiveCollaborators(
            mapActiveCollaborators(
              response.data.result || {},
              ACTIVE_COLLABORATORS_LIMIT,
            ),
          )
        } else {
          toastApiError(response, "Failed to load active collaborators")
          setActiveCollaborators([])
        }
      } catch (err) {
        if (!cancelled) {
          toastApiError(err, "Failed to load active collaborators")
          setActiveCollaborators([])
        }
      } finally {
        if (!cancelled) setIsCollaboratorsLoading(false)
      }
    }
    fetchActiveCollaborators()
    return () => {
      cancelled = true
    }
  }, [canViewMembers])

  const adminName = getUserFullName(currentUser, { fallback: "Admin" })

  const greetingTitle = useMemo(
    () => formatDashboardGreeting(adminName),
    [adminName],
  )

  return (
    <div className="admin-page admin-dashboard">
      <PageHeader
        className="admin-dashboard__header admin-page__header"
        title={greetingTitle}
        subtitle="Glad you're here! What are you going to work on today?"
        actions={
          <div className="admin-dashboard__period" ref={periodRef}>
            <CustomButton
              variant="transparent"
              size="md"
              type="button"
              trailingIconSrc={ICONS.chevronDown}
              trailingIconAlt=""
              aria-haspopup="listbox"
              aria-expanded={periodOpen}
              onClick={() => setPeriodOpen(open => !open)}
            >
              {periodLabel}
            </CustomButton>
            {periodOpen ? (
              <ul className="admin-dashboard__period-menu" role="listbox">
                {PERIOD_OPTIONS.map(option => (
                  <li key={option.value}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={option.value === period}
                      className={
                        option.value === period
                          ? "admin-dashboard__period-option admin-dashboard__period-option--active"
                          : "admin-dashboard__period-option"
                      }
                      onClick={() => {
                        setPeriod(option.value)
                        setPeriodOpen(false)
                      }}
                    >
                      {option.label}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        }
      />

      {error && !isLoading ? (
        <EmptyState
          title="Unable to load dashboard"
          description={error}
          actionText="Try again"
          onActionClick={fetchDashboard}
        />
      ) : null}

      <div
        className="admin-dashboard__kpi-grid"
        aria-busy={isLoading || undefined}
      >
        {kpiCards.map(card => (
          <DashboardCard
            key={card.key}
            title={card.title}
            tone={card.tone}
            className="admin-dashboard__kpi-card"
            topRight={
              !card.module || can(card.module, "view") ? (
                <CustomButton
                  variant="transparent"
                  size="sm"
                  type="button"
                  trailingIconSrc={ICONS.arrowRight12}
                  trailingIconAlt=""
                  onClick={() => navigate(card.to)}
                >
                  View All
                </CustomButton>
              ) : null
            }
          >
            <div className="admin-dashboard__kpi-footer">
              <span className="admin-dashboard__kpi-value">
                {isLoading ? "…" : card.value}
              </span>
              <span className="admin-dashboard__kpi-delta">
                {isLoading ? "" : card.delta}
              </span>
            </div>
          </DashboardCard>
        ))}
      </div>

      <div className="admin-dashboard__main">
        <div className="admin-dashboard__row admin-dashboard__row--overview">
          <DashboardCard
            title="Platform Overview"
            tone="lightblue-custom"
            className="admin-dashboard__widget admin-dashboard__widget--chart admin-dashboard__span-main"
            topRight={
              <div className="admin-dashboard__legend">
                {platformOverview.series.map(item => (
                  <span key={item.key} className="admin-dashboard__legend-item">
                    <span
                      className="admin-dashboard__legend-dot"
                      style={{ backgroundColor: item.color }}
                      aria-hidden="true"
                    />
                    {item.label}
                  </span>
                ))}
              </div>
            }
          >
            <PlatformOverviewChart
              labels={platformOverview.labels}
              series={platformOverview.series}
            />
          </DashboardCard>

          <DashboardCard
            title="Members by City"
            subtitle="Top 5"
            tone="pinkbrown"
            className="admin-dashboard__widget admin-dashboard__widget--city admin-dashboard__span-side"
            topRight={
              <MembersByCityDonut
                segments={membersByCity.segments}
                otherPercent={membersByCity.otherPercent}
              />
            }
          >
            <ul className="admin-dashboard__city-list">
              {membersByCity.segments.map(city => (
                <li
                  key={city.id || city.name}
                  className="admin-dashboard__city-row"
                >
                  <span
                    className="admin-dashboard__city-dot"
                    style={{ backgroundColor: city.color }}
                    aria-hidden="true"
                  />
                  <span className="admin-dashboard__city-name">
                    {city.name}
                  </span>
                  <span className="admin-dashboard__city-pct">
                    {city.percent}%
                  </span>
                </li>
              ))}
            </ul>
            <div className="admin-dashboard__city-other">
              <span>Other Cities</span>
              <span>{membersByCity.otherPercent}%</span>
            </div>
          </DashboardCard>
        </div>

        <div className="admin-dashboard__row">
          <DashboardCard
            title="Top Modules Usage"
            tone="darkblue"
            className="admin-dashboard__widget admin-dashboard__span-main"
          >
            {moduleUsage.length === 0 && !isLoading ? (
              <EmptyState
                title="No module usage"
                description="Usage data will show up here once available."
              />
            ) : (
              <ul className="admin-dashboard__modules">
                {moduleUsage.map(module => (
                  <li key={module.key} className="admin-dashboard__module">
                    <div className="admin-dashboard__module-meta">
                      <span className="admin-dashboard__module-label">
                        {module.label}
                      </span>
                      <span className="admin-dashboard__module-pct">
                        {module.percent}%
                      </span>
                    </div>
                    <div
                      className="admin-dashboard__module-track"
                      role="progressbar"
                      aria-valuenow={module.percent}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-label={`${module.label} usage`}
                    >
                      <div
                        className="admin-dashboard__module-fill"
                        style={{
                          width: `${module.percent}%`,
                          backgroundColor: module.color,
                        }}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </DashboardCard>

          {canViewActivityLogs ? (
            <DashboardCard
              title="Recent Activity"
              tone="lightteal"
              className="admin-dashboard__widget admin-dashboard__activity admin-dashboard__span-side"
              topRight={
                <CustomButton
                  variant="primary"
                  size="sm"
                  type="button"
                  trailingIconSrc={ICONS.arrowRight}
                  trailingIconAlt=""
                  onClick={() => navigate("/admin/activity-log")}
                >
                  View Logs
                </CustomButton>
              }
            >
              {isActivityLoading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <div
                    key={`activity-skel-${index}`}
                    className="admin-dashboard__activity-row admin-dashboard__activity-row--skeleton"
                    aria-hidden="true"
                  >
                    <div className="admin-dashboard__skeleton" />
                  </div>
                ))
              ) : recentActivity.length === 0 ? (
                <EmptyState
                  title="No recent activity"
                  description="Activity will show up here once available."
                />
              ) : (
                recentActivity.map(item => (
                  <ListItem
                    key={item.id}
                    dotColor={item.dotColor}
                    title={item.title}
                    subtitle={item.time}
                    bordered
                  />
                ))
              )}
            </DashboardCard>
          ) : null}
        </div>

        <div className="admin-dashboard__row admin-dashboard__row--bottom">
          <DashboardCard
            title="Cities"
            className="dcard--no-spacing dcard--no-justify admin-dashboard__cities-card admin-dashboard__span-main"
            topRight={
              canViewCities ? (
                <CustomButton
                  variant="primary"
                  size="sm"
                  type="button"
                  trailingIconSrc={ICONS.arrowRight}
                  trailingIconAlt=""
                  onClick={() => navigate("/admin/cities")}
                >
                  View All
                </CustomButton>
              ) : null
            }
          >
              <div className="admin-dashboard__cities-table" role="table">
                <div
                  className="admin-dashboard__cities-row admin-dashboard__cities-row--head"
                  role="row"
                >
                  <div role="columnheader">City Name</div>
                  <div role="columnheader">State</div>
                  <div role="columnheader">Members</div>
                  <div role="columnheader">Active Projects</div>
                  <div role="columnheader">Added Date</div>
                  <div role="columnheader">Actions</div>
                </div>
                {isCitiesLoading
                  ? Array.from({ length: 4 }).map((_, index) => (
                      <div
                        key={`city-skel-${index}`}
                        className="admin-dashboard__cities-row admin-dashboard__cities-row--skeleton"
                        role="row"
                      >
                        <div className="admin-dashboard__skeleton" />
                        <div className="admin-dashboard__skeleton" />
                        <div className="admin-dashboard__skeleton" />
                        <div className="admin-dashboard__skeleton" />
                        <div className="admin-dashboard__skeleton" />
                        <div className="admin-dashboard__skeleton" />
                      </div>
                    ))
                  : null}
                {!isCitiesLoading && activeCities.length === 0 ? (
                  <div className="admin-dashboard__cities-empty">
                    <EmptyState
                      title="No cities"
                      description="Cities will show up here once available."
                    />
                  </div>
                ) : null}
                {!isCitiesLoading &&
                  activeCities.map(city => (
                    <div
                      key={city.id}
                      className="admin-dashboard__cities-row"
                      role="row"
                    >
                      <div className="admin-dashboard__city-cell" role="cell">
                        {city.thumbnail ? (
                          <img
                            src={city.thumbnail}
                            alt=""
                            className="admin-dashboard__city-avatar admin-dashboard__city-avatar--image"
                          />
                        ) : (
                          <span
                            className="admin-dashboard__city-avatar"
                            aria-hidden="true"
                          >
                            {cityInitials(city.name)}
                          </span>
                        )}
                        <span>{city.name}</span>
                      </div>
                      <div role="cell">{city.state}</div>
                      <div role="cell">{city.members}</div>
                      <div role="cell">{city.projects}</div>
                      <div role="cell">{city.addedDate}</div>
                      <div role="cell">
                        {canViewCities ? (
                          <CustomButton
                            variant="transparent"
                            size="sm"
                            type="button"
                            trailingIconSrc={ICONS.arrowRight12}
                            trailingIconAlt=""
                            onClick={() =>
                              navigate(`/admin/cities/${city.id}`, {
                                state: {
                                  city: {
                                    id: city.id,
                                    name: city.name,
                                    state: city.state,
                                    country: city.country || "",
                                  },
                                },
                              })
                            }
                          >
                            View City
                          </CustomButton>
                        ) : null}
                      </div>
                    </div>
                  ))}
              </div>
            </DashboardCard>

          <DashboardCard
            title="Active Collaborators"
            tone="pinkbrown"
            className="dcard--no-justify admin-dashboard__collaborators admin-dashboard__span-side"
            topRight={<span className="admin-dashboard__top5">Top 5</span>}
          >
            <div
              className="admin-dashboard__collaborators-list"
              aria-busy={isCollaboratorsLoading || undefined}
            >
              {isCollaboratorsLoading
                ? Array.from({ length: 5 }).map((_, index) => (
                    <div
                      key={`collaborator-skel-${index}`}
                      className="admin-dashboard__collaborator-row admin-dashboard__collaborator-row--skeleton"
                      aria-hidden="true"
                    >
                      <div className="admin-dashboard__skeleton" />
                    </div>
                  ))
                : null}

              {!isCollaboratorsLoading && activeCollaborators.length === 0 ? (
                <EmptyState
                  title="No collaborators yet"
                  description="Top collaborators will show up here once available."
                />
              ) : null}

              {!isCollaboratorsLoading
                ? activeCollaborators
                    .slice(0, 5)
                    .map(person => (
                      <ListItem
                        key={person.id}
                        avatarName={person.name}
                        avatarSrc={person.avatarSrc}
                        title={person.name}
                        trailing={
                          <span className="admin-dashboard__role">
                            {person.role}
                          </span>
                        }
                        className="list-item--collaborator"
                        bordered
                      />
                    ))
                : null}
            </div>
          </DashboardCard>
        </div>
      </div>
    </div>
  )
}

export default AdminDashboard
